interface DocumentPreviewProps {
  url: string;
  mimeType?: string;
  fileName?: string;
}

/**
 * Helper function to get the appropriate icon component type for a document type
 */
export const getDocumentIcon = (mimeType?: string): string => {
  if (!mimeType) return 'AttachmentIcon';

  if (
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    mimeType.includes('vnd.openxmlformats-officedocument.wordprocessingml')
  ) {
    return 'DescriptionIcon';
  }

  if (
    mimeType.includes('sheet') ||
    mimeType.includes('excel') ||
    mimeType.includes('vnd.openxmlformats-officedocument.spreadsheetml')
  ) {
    return 'TableChartIcon';
  }

  if (
    mimeType.includes('presentation') ||
    mimeType.includes('powerpoint') ||
    mimeType.includes('vnd.openxmlformats-officedocument.presentationml')
  ) {
    return 'PresentToAllIcon';
  }

  return 'AttachmentIcon';
};

/**
 * Helper function to get the preview URL for Office documents using Office 365 Viewer
 */
export const getOfficePreviewUrl = (url: string): string => {
  const encodedUrl = encodeURIComponent(url);
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
};

/**
 * Helper function to check if a file type supports online preview
 */
export const supportsOnlinePreview = (mimeType?: string): boolean => {
  if (!mimeType) return false;

  const supportedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.ms-powerpoint',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-pdf',
    'text/plain',
  ];

  return supportedTypes.some((type) =>
    mimeType.toLowerCase().includes(type.toLowerCase())
  );
};

/**
 * Helper function to extract file name from URL
 */
export const extractFileName = (url: string): string => {
  try {
    const urlParts = url.split('/');
    let fileName = urlParts[urlParts.length - 1];
    fileName = decodeURIComponent(fileName.replace(/\+/g, ' '));
    // Remove query parameters if any
    fileName = fileName.split('?')[0];
    return fileName;
  } catch (error) {
    return 'Document';
  }
};

/**
 * Helper function to get human-readable file type from mimeType
 */
export const getFileTypeLabel = (mimeType?: string): string => {
  if (!mimeType) return 'File';

  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word') || mimeType.includes('wordprocessingml'))
    return 'Word';
  if (mimeType.includes('sheet') || mimeType.includes('spreadsheetml'))
    return 'Spreadsheet';
  if (
    mimeType.includes('presentation') ||
    mimeType.includes('presentationml')
  )
    return 'Presentation';
  if (mimeType.includes('csv')) return 'CSV';
  if (mimeType.includes('text')) return 'Text';
  if (mimeType.includes('image')) return 'Image';
  if (mimeType.includes('audio')) return 'Audio';
  if (mimeType.includes('video')) return 'Video';

  return 'File';
};

export default {
  getDocumentIcon,
  getOfficePreviewUrl,
  supportsOnlinePreview,
  extractFileName,
  getFileTypeLabel,
};
